import { useQuery } from "@tanstack/react-query"

import {
  v2CustomerDirections,
  type V2CustomerDirectionsResponse,
} from "@be-miniapp/api"
import { BEAutoRideStatus, type FullEngagementInfo } from "@be-miniapp/types"
import { useRouteOptimization } from "@be-miniapp/ui/hooks/use-route-optimization"

export interface UseRideNavigationOptions {
  engagement?: FullEngagementInfo
  pickup?: { location?: { latitude?: number; longitude?: number } }
  dropoff?: { location?: { latitude?: number; longitude?: number } }
  currentState: string
  enableRouteOptimization?: boolean
  routeOptimizationDebug?: boolean
}

export interface UseRideNavigationResult {
  routeUserDriver: V2CustomerDirectionsResponse | undefined
  routePerfectRide: V2CustomerDirectionsResponse | undefined
  routeDriverDropoff: V2CustomerDirectionsResponse | undefined
  isPerfectRide: boolean
  routeOptimization: ReturnType<typeof useRouteOptimization>
}

export const useRideNavigation = ({
  engagement,
  pickup,
  dropoff,
  currentState,
  enableRouteOptimization = true,
  routeOptimizationDebug = false,
}: UseRideNavigationOptions): UseRideNavigationResult => {
  const isPerfectRide =
    (!!engagement?.in_perfect_ride || !!engagement?.driver_in_perfect_ride) &&
    engagement?.status === BEAutoRideStatus.accepted

  const routeOptimization = useRouteOptimization({
    enabled: enableRouteOptimization,
    enableDebug: routeOptimizationDebug,
    config: {
      minDeviationDistance: 75,
      moderateDeviationDistance: 150,
      majorDeviationDistance: 300,
      minDeviationTime: 20000,
      maxDeviationTime: 90000,
      consecutiveDeviationsThreshold: 3,
      confidenceThreshold: 65,
      cooldownPeriod: 45000,
      considerSpeed: true,
      considerBearing: true,
      urbanTolerance: 2.0,
    },
  })

  const { data: routeUserDriver } = useQuery({
    queryKey: [
      "routeUserDriver",
      isPerfectRide,
      currentState,
      engagement?.current_location_latitude,
      engagement?.current_location_longitude,
      engagement?.perfect_latitude,
      pickup?.location?.latitude,
      engagement?.perfect_longitude,
      pickup?.location?.longitude,
      engagement?.vehicle_type,
    ],
    queryFn: async () => {
      const e = engagement as FullEngagementInfo
      return v2CustomerDirections({
        origin: {
          latitude: e.current_location_latitude ?? 0,
          longitude: e.current_location_longitude ?? 0,
        },
        destination: {
          latitude: isPerfectRide
            ? (e.perfect_latitude ?? 0)
            : (pickup?.location?.latitude ?? 0),
          longitude: isPerfectRide
            ? (e.perfect_longitude ?? 0)
            : (pickup?.location?.longitude ?? 0),
        },
        purpose: "draw",
        phase: "arriving",
        vehicle_type: e.vehicle_type ?? 0,
      })
    },
    enabled:
      (currentState === "driver-found" || currentState === "perfect-ride") &&
      !!engagement,
  })

  const { data: routePerfectRide } = useQuery({
    queryKey: [
      "routePerfectRide",
      engagement?.perfect_latitude,
      engagement?.perfect_longitude,
      engagement?.pickup_latitude,
      engagement?.pickup_longitude,
      engagement?.vehicle_type,
    ],
    queryFn: async () => {
      const e = engagement as FullEngagementInfo
      return v2CustomerDirections({
        origin: {
          latitude: e.perfect_latitude ?? 0,
          longitude: e.perfect_longitude ?? 0,
        },
        destination: {
          latitude: e.pickup_latitude ?? 0,
          longitude: e.pickup_longitude ?? 0,
        },
        purpose: "draw",
        phase: "arriving",
        vehicle_type: e.vehicle_type ?? 0,
      })
    },
    enabled: !!engagement && isPerfectRide,
  })

  const { data: routeDriverDropoff } = useQuery({
    queryKey: [
      "routeDriverDropoff",
      currentState,
      engagement?.current_location_latitude,
      engagement?.current_location_longitude,
      dropoff?.location?.latitude,
      dropoff?.location?.longitude,
      engagement?.vehicle_type,
    ],
    queryFn: async () => {
      const e = engagement as FullEngagementInfo
      return v2CustomerDirections({
        origin: {
          latitude: e.current_location_latitude ?? 0,
          longitude: e.current_location_longitude ?? 0,
        },
        destination: {
          latitude: dropoff?.location?.latitude ?? 0,
          longitude: dropoff?.location?.longitude ?? 0,
        },
        purpose: "draw",
        phase: "in-trip",
        vehicle_type: e.vehicle_type ?? 0,
      })
    },
    enabled:
      currentState === "ride-started" &&
      !!engagement &&
      !!dropoff?.location?.latitude &&
      !!dropoff?.location?.longitude,
  })

  return {
    routeUserDriver,
    routePerfectRide,
    routeDriverDropoff,
    isPerfectRide,
    routeOptimization,
  }
}
